import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Crown, Hourglass, ArrowRight, LifeBuoy } from 'lucide-react';
import { Navbar } from '../Layout';
import { useTrial } from '../../contexts/TrialContext';
import OneClickUpgrade from './OneClickUpgrade';

/**
 * Full-page lock for trial students who have not paid (status "unpaid") or whose trial ran out ("expired").
 * Rendered by ProtectedRoute in place of every learning page; settings / tickets / checkout stay reachable.
 */
export default function TrialWall({ state }) {
  const { cfg, price, hours } = useTrial();
  const expired = state?.status === 'expired';
  const title = expired ? (cfg.wall_expired_title || 'Your trial has ended') : (cfg.wall_unpaid_title || 'Complete your trial payment');
  const text = expired
    ? (cfg.wall_expired_text || `Your ${hours}-hour trial is over. Upgrade to Pro to keep every mock test, AI scoring, all courses and live classes.`)
    : (cfg.wall_unpaid_text || `Your account is ready. Pay ${price} once to unlock ${hours} hours of the course, mock tests and the free group call.`);
  const button = expired ? (cfg.wall_expired_button || 'Upgrade to Pro') : (cfg.wall_unpaid_button || `Pay ${price} and start my trial`);
  const to = expired ? '/checkout' : '/checkout?plan=trial';
  const ended = state?.expires_at ? new Date(state.expires_at) : null;
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 py-12 sm:py-20" data-testid="trial-wall" data-state={state?.status || ''}>
        <div className="rounded-3xl bg-white border border-slate-200 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.35)] p-7 sm:p-10 text-center">
          <div className={`w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center ${expired ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-600'}`}>
            {expired ? <Hourglass size={30} /> : <Lock size={30} />}
          </div>
          <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400">{expired ? 'Trial over' : 'One step left'}</p>
          <h1 className="font-[Manrope] text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2" data-testid="trial-wall-title">{title}</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-3 leading-relaxed" data-testid="trial-wall-text">{text}</p>
          {expired && ended && (
            <p className="text-xs text-slate-400 mt-2" data-testid="trial-wall-ended-at">Ended {ended.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</p>
          )}
          {expired ? (
            <OneClickUpgrade variant="card" fallback={(
              <Link to={to} className="mt-7 inline-flex items-center justify-center gap-2 w-full rounded-2xl bg-slate-900 text-white font-bold text-base px-6 py-4 hover:bg-slate-800 transition-colors" data-testid="trial-wall-cta">
                <Crown size={18} className="text-amber-400" />{button}
              </Link>
            )} />
          ) : (
            <Link to={to} className="mt-7 inline-flex items-center justify-center gap-2 w-full rounded-2xl bg-slate-900 text-white font-bold text-base px-6 py-4 hover:bg-slate-800 transition-colors" data-testid="trial-wall-cta">
              <ArrowRight size={18} />{button}
            </Link>
          )}
          {!expired && (
            <Link to="/checkout" className="mt-3 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="trial-wall-pro-link">
              <Crown size={14} className="text-amber-500" />Or go straight to Pro — everything, unlimited
            </Link>
          )}
          {expired && (
            <Link to="/whats-included" className="mt-3 inline-flex items-center justify-center text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="trial-wall-pro-link">
              {cfg.wall_pro_link || "See what's in Pro"}
            </Link>
          )}
          <p className="mt-8 text-xs text-slate-400 flex items-center justify-center gap-1.5">
            <LifeBuoy size={12} />Need help? <Link to="/contact" className="underline underline-offset-2 hover:text-slate-700" data-testid="trial-wall-contact-link">Contact us</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
